import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { Badge } from '../../entities/badge';
import { Language } from '../../entities/language';
import { Pagination } from '../../entities/pagination';
import PaginationComponent from '../common/pagination';
import { strings } from '../../localization/strings';

interface ComponentProps {
  badges: Badge[];
  languages: Language[];
  pagination: Pagination;
  onUpdate: (pageNo: number) => void;
}

const getTitle = (badge: Badge) => {
  return badge.translations.find((translation) => translation.is_active)?.title || strings.badges.list.untitled;
};

const getActiveLanguages = (badge: Badge, languages: Language[]) => {
  const languageCodes = new Map(languages.map((language) => [language.id, language.key.toUpperCase()]));
  const activeLanguages = badge.translations
    .filter((translation) => translation.is_active)
    .map((translation) => languageCodes.get(translation.language_id))
    .filter(Boolean);

  return activeLanguages.length > 0 ? activeLanguages.join(', ') : strings.badges.list.noActiveLanguages;
};

export default function BadgesTableComponent({ badges, languages, pagination, onUpdate }: ComponentProps) {
  return (
    <>
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-4 lg:px-8">
            <div className="overflow-hidden border-b border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {strings.badges.list.columns.title}
                    </th>
                    <th
                      scope="col"
                      className="w-36 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {strings.badges.list.columns.color}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {strings.badges.list.columns.activeLanguages}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {badges.map((badge, index) => (
                    <tr key={badge.id} className={classNames(index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <Link to={`/badges/edit/${badge.id}`} className="text-indigo-600 hover:text-indigo-900">
                          {getTitle(badge)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-3">
                          <span
                            className="inline-block h-4 w-4 rounded-full border border-gray-300"
                            style={{ backgroundColor: badge.color }}
                          />
                          <span>{badge.color}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{getActiveLanguages(badge, languages)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PaginationComponent pagination={pagination} onChangePage={onUpdate} />
    </>
  );
}
